import {
	useMutation,
	useQuery,
	useQueryClient,
	keepPreviousData,
} from "@tanstack/react-query"
import { ErrorResponse, SuccessResponse, useHttpClient } from "api/api"
import {
	ConfigClinicalNoteLabelSets,
	ConfigInternalEmailDomains,
	ConfigResources,
	GlobalConfig,
} from "interface/globalConfig"
import { Icd10Code } from "interface/patientType"
import { useMemo } from "react"
import { ICD10_QUERY_MIN_LENGTH } from "utils/constants"

const queryKeys = {
	GetGlobalConfig: "get-global-config",
	GetAllGlobalConfigs: "get-all-global-configs",
	GetClinicalLabels: "get-clinical-labels",
	GetIcd10Codes: "get-icd10-codes",
}

export function useAllGlobalConfigs() {
	const { httpClient } = useHttpClient()

	return useQuery<GlobalConfig[], ErrorResponse>({
		queryKey: [queryKeys.GetAllGlobalConfigs],
		async queryFn({ signal }) {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<GlobalConfig[]>>(
				"/api/v1/admin/global-config",
				{ signal },
			)
			return data
		},
	})
}

export const useConfigAdmin = () => {
	const { data = [] } = useAllGlobalConfigs()

	return useMemo(
		() =>
			data
				.filter((curr) => curr.group === "ADMIN_PERMISSIONS")
				.reduce(
					(prev, curr) => {
						const { resources } = curr.data as ConfigResources
						const { internalEmailDomains } =
							curr.data as ConfigInternalEmailDomains
						if (!!resources) {
							return { ...prev, resources }
						} else if (!!internalEmailDomains) {
							return { ...prev, internalEmailDomains }
						}

						return prev
					},
					{ resources: [], internalEmailDomains: [] } as {
						resources: ConfigResources["resources"]
						internalEmailDomains: ConfigInternalEmailDomains["internalEmailDomains"]
					},
				),
		[data],
	)
}

export const useConfigClinicalNoteLabels = () => {
	const { httpClient } = useHttpClient()

	return useQuery<ConfigClinicalNoteLabelSets, ErrorResponse>({
		queryKey: [queryKeys.GetClinicalLabels],
		async queryFn({ signal }) {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<ConfigClinicalNoteLabelSets>>(
				"/api/v1/clinical-note-labels",
				{ signal },
			)
			return data
		},
	})
}

export const useIcd10CodeSearch = (query: string) => {
	const { httpClient } = useHttpClient()
	const trimmedQuery = query.trim()

	return useQuery<Icd10Code[], ErrorResponse>({
		queryKey: [queryKeys.GetIcd10Codes, trimmedQuery],
		async queryFn({ signal }) {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<Icd10Code[]>>(
				"/api/v1/icd10-codes",
				{ signal, params: { query: trimmedQuery } },
			)
			return data
		},
		enabled: trimmedQuery.length >= ICD10_QUERY_MIN_LENGTH,
		placeholderData: keepPreviousData,
	})
}

export function useGlobalConfigUpdate() {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<
		GlobalConfig,
		ErrorResponse,
		{ id: string; group?: string; code?: string; data: any }
	>({
		async mutationFn({ id, group, code, data }) {
			const response = await httpClient.patch<SuccessResponse<GlobalConfig>>(
				`/api/v1/admin/global-config/${id}`,
				{ group, code, data },
			)
			return response.data.data
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.GetAllGlobalConfigs],
			})
			if (variables.group && variables.code) {
				queryClient.invalidateQueries({
					queryKey: [
						queryKeys.GetGlobalConfig,
						variables.group,
						variables.code,
					],
				})
			}
		},
	})
}
